import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "./ui";
import { apiErrorText } from "@/lib/api";
import { useAuth } from "./auth";
import { goAnchor } from "./app-nav";
import { contactsApi, showContact, type Contact } from "./contacts-api";
import { PERSON_ROLES, aed, companiesApi, showCompany, type BazaDocuments, type Company, type CompanyInput, type HistoryItem, type TimelineItem } from "./companies-api";
import { financeApi, type CustomerFinance } from "./finance-api";
import { FinanceBlock } from "./finance";
import { ContactDialogs } from "./contact-dialogs";
import { NotesBlock, TasksBlock } from "./activity";
import { dealsApi, money, showDeal, showNewDeal, type Deal } from "./deals-api";
import { showConversation, showNewDialog } from "./inbox-api";
import { channelLabel } from "./ads";
import { tagColors } from "./tags-meta";
import { OwnerControl } from "./owner-control";
import { BazaAsk, useBazaEnabled } from "./baza-ask";
import { locale, t, tn } from "@/lib/i18n";

/* Карточка клиента по макету «Hub — клиент в одном окне»: шапка с действиями, пять плиток (выручка, 12 месяцев, дебиторка,
   средний чек, открытые сделки), вкладки «Лента / Переписка / Сделки / Документы Books / Задачи и заметки / История»,
   справа — контактные лица, поля, клиенты Books и реквизиты. Данные — /companies/{id}, /companies/{id}/timeline|history|conversations,
   /finance/customer?company=, /deals?company=, /notes и /tasks ?company= (люди и сделки компании включены). */

const day = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const when = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? t("сегодня {time}", { time: d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) }) : d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
};
const input =
  "h-9 w-full rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground outline-none transition focus:border-primary disabled:opacity-60";
const btn = "inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn px-3 text-[12.5px] font-semibold transition disabled:cursor-default disabled:opacity-50";
const btnPrimary = `${btn} bg-primary text-primary-foreground hover:brightness-110`;
const btnGhost = `${btn} border border-border bg-background text-foreground hover:bg-muted`;

const FIELDS: { key: keyof CompanyInput; label: string; wide?: boolean }[] = [
  { key: "name", label: t("Название") },
  { key: "trn", label: "TRN" },
  { key: "phone", label: t("Телефон") },
  { key: "email", label: t("Почта") },
  { key: "segment", label: t("Сегмент") },
  { key: "country", label: t("Страна") },
  { key: "city", label: t("Город") },
  { key: "website", label: t("Сайт") },
  { key: "address", label: t("Адрес"), wide: true },
  { key: "notes", label: t("Заметки"), wide: true },
];

type Tab = "feed" | "dialogs" | "deals" | "docs" | "activity" | "history";

const FEED_FILTERS: { id: string; label: string; kinds: TimelineItem["kind"][] }[] = [
  { id: "messages", label: t("сообщения"), kinds: ["message"] },
  { id: "docs", label: t("документы"), kinds: ["invoice", "order", "creditnote"] },
  { id: "payments", label: t("оплаты"), kinds: ["payment"] },
  { id: "deals", label: t("сделки"), kinds: ["deal"] },
  { id: "notes", label: t("заметки"), kinds: ["note", "task"] },
];

export function CompanyCard({ id, onBack, flash }: { id: string; onBack: () => void; flash: (t: string) => void }) {
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [c, setC] = useState<Company | null>(null);
  const [fin, setFin] = useState<CustomerFinance | null>(null);
  const [tab, setTab] = useState<Tab>("feed");
  const [showForm, setShowForm] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [askBaza, setAskBaza] = useState(false);
  const bazaOn = useBazaEnabled();

  const load = useCallback(async () => {
    try {
      const x = await companiesApi.get(id);
      setC(x);
      if ((x.zoho ?? []).length) financeApi.customer({ companyId: id }).then(setFin, () => setFin(null));
      else setFin(null);
    } catch (e) {
      flash(apiErrorText(e));
    }
  }, [id, flash]);

  useEffect(() => {
    setTab("feed");
    void load();
  }, [load]);

  if (!c) return <p className="p-5 text-[12.5px] text-muted-foreground">{t("Загрузка…")}</p>;

  const people = c.contacts ?? [];
  const primary = people.find((p) => p.phone) ?? people[0];
  const s = fin?.summary;
  const cur = s?.currency ?? c.deals?.currency ?? "AED";
  const growth = s && s.revenuePrev12m > 0 ? Math.round(((s.revenue12m - s.revenuePrev12m) / s.revenuePrev12m) * 100) : null;
  const deals = c.deals;

  /* окно «Новый диалог» закрыли без отправки — вернуться в эту карточку */
  const backHere = () => {
    showCompany(c.id);
    goAnchor("#companies");
  };

  /* «Написать в WhatsApp»: есть открытый диалог WhatsApp с людьми компании — открываем его, иначе новый диалог на номер закупщика. */
  const writeWhatsApp = async () => {
    try {
      const r = await companiesApi.conversations(c.id);
      const wa = r.items.find((x) => x.channel === "whatsapp" && !x.archive);
      if (wa) {
        showConversation(wa.id);
        goAnchor("#inbox-app");
        return;
      }
    } catch {
      /* нет доступа к диалогам — пишем первыми */
    }
    if (!primary?.phone) {
      flash(t("У людей компании нет номера телефона — добавьте его в карточку человека."));
      return;
    }
    showNewDialog(primary.phone, backHere);
    goAnchor("#inbox-app");
  };

  /* «Написать письмо»: есть диалог почты с людьми компании — открываем его, иначе новый диалог на адрес первого человека с почтой. */
  const writeEmail = async () => {
    try {
      const r = await companiesApi.conversations(c.id);
      const em = r.items.find((x) => x.channel === "email");
      if (em) {
        showConversation(em.id);
        goAnchor("#inbox-app");
        return;
      }
    } catch {
      /* нет доступа к диалогам — пишем первыми */
    }
    const person = people.find((p) => p.email);
    if (!person?.email) {
      flash(t("У людей компании нет адреса почты — добавьте его в карточку человека."));
      return;
    }
    showNewDialog(person.email, backHere);
    goAnchor("#inbox-app");
  };

  const tiles: { label: string; value: string; sub: string; tone?: string }[] = [
    { label: t("Выручка за всё время"), value: s ? money(s.revenue, cur) : c.revenue ? aed(c.revenue) : "—", sub: s ? `${tn(s.invoices, "счёт", "счёта", "счетов")}${s.firstInvoiceAt ? ` · ${t("с {date}", { date: day(s.firstInvoiceAt) })}` : ""}` : "" },
    {
      label: t("Последние 12 месяцев"),
      value: s ? money(s.revenue12m, cur) : "—",
      sub: growth === null ? (s ? t("раньше счетов не было") : "") : t("{pct}% к прошлым 12", { pct: `${growth >= 0 ? "+" : ""}${growth}` }),
      tone: growth === null ? "" : growth >= 0 ? "text-emerald-600" : "text-red-600",
    },
    {
      label: t("Дебиторка"),
      value: s ? (s.receivable > 0.5 ? money(s.receivable, cur) : t("нет")) : c.receivable ? aed(c.receivable) : t("нет"),
      sub: s && s.overdue > 0.5 ? t("просрочено {d} · {n}", { d: tn(s.overdueDays, "день", "дня", "дней"), n: tn(s.openInvoices, "счёт", "счёта", "счетов") }) : s?.openInvoices ? t("{n} к оплате", { n: tn(s.openInvoices, "счёт", "счёта", "счетов") }) : "",
      tone: s && s.overdue > 0.5 ? "text-red-600" : s && s.receivable > 0.5 ? "text-amber-600" : "",
    },
    { label: t("Средний чек"), value: s && s.avgCheck ? money(s.avgCheck, cur) : "—", sub: s?.avgPayDays !== null && s?.avgPayDays !== undefined ? t("оплата в среднем за {d}", { d: tn(s.avgPayDays, "день", "дня", "дней") }) : "" },
    {
      label: t("Открытые сделки"),
      value: deals ? (deals.open ? `${deals.open} · ${money(deals.openSum, deals.currency)}` : "0") : "—",
      sub: deals && deals.total ? t("выиграно {won} из {total}", { won: deals.won, total: deals.total }) : t("сделок пока нет"),
    },
  ];

  const tabs: { id: Tab; label: string }[] = [
    { id: "feed", label: t("Лента") },
    { id: "dialogs", label: `${t("Переписка")}${c.conversations ? ` · ${c.conversations}` : ""}` },
    { id: "deals", label: `${t("Сделки")}${deals?.total ? ` · ${deals.total}` : ""}` },
    { id: "docs", label: `${t("Заказы, счета и оплаты")}${fin?.total ? ` · ${fin.total}` : ""}` },
    { id: "activity", label: t("Задачи и заметки") },
    { id: "history", label: t("История изменений") },
  ];

  return (
    <div className="p-3.5 md:p-5" data-testid="company-card">
      <button type="button" onClick={onBack} className="inline-flex cursor-pointer items-center gap-1 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground">
        <i className="ri-arrow-left-line leading-none" aria-hidden />
        {t("Все компании")}
      </button>

      {/* шапка: название, реквизиты одной строкой, чипы, действия */}
      <div className="mt-3 flex flex-wrap items-start gap-3">
        <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-[15px] font-bold text-primary" aria-hidden>
          {initials(c.name)}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold leading-snug">{c.name}</h3>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            {[t("Компания"), c.segment, c.department, [c.address, c.city].filter(Boolean).join(", "), c.trn ? `TRN ${c.trn}` : ""].filter(Boolean).join(" · ")}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {c.salesperson ? <Chip>{t("Продавец Zoho: {name}", { name: c.salesperson })}</Chip> : null}
            {c.zcrmAccountId ? <Chip ok>Zoho CRM ✓</Chip> : null}
            {(c.zoho ?? []).length ? <Chip ok>Zoho Books ✓</Chip> : <Chip>{t("Не связана с Books")}</Chip>}
          </div>
          <div className="mt-2">
            <OwnerControl kind="companies" id={c.id} owner={c.owner ?? null} onChanged={(o) => setC({ ...c, owner: o, ownerId: o?.id ?? null })} />
          </div>
        </div>
        {!readOnly ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void writeWhatsApp()} className={btnPrimary}>
              <i className="ri-whatsapp-line" aria-hidden />
              {t("Написать в WhatsApp")}
            </button>
            <button type="button" onClick={() => void writeEmail()} className={btnGhost} data-testid="company-write-email">
              <i className="ri-mail-send-line" aria-hidden />
              {t("Написать письмо")}
            </button>
            <button
              type="button"
              onClick={() => {
                showNewDeal({ companyId: c.id, contactId: primary?.id ?? null });
                goAnchor("#deals");
              }}
              className={btnGhost}
            >
              {t("Новая сделка")}
            </button>
            <button type="button" onClick={() => setTab("activity")} className={btnGhost}>
              {t("Задача")}
            </button>
            {bazaOn ? (
              <button type="button" onClick={() => setAskBaza((v) => !v)} aria-pressed={askBaza} className={btnGhost} data-testid="company-baza-ask">
                <i className="ri-book-open-line" aria-hidden />
                {t("Спросить базу знаний")}
              </button>
            ) : null}
            <button type="button" onClick={() => setMergeOpen((v) => !v)} aria-pressed={mergeOpen} className={btnGhost} title={t("Слить другую компанию в эту карточку")} data-testid="company-merge">
              <i className="ri-git-merge-line" aria-hidden />
              {t("Слить с…")}
            </button>
          </div>
        ) : null}
      </div>
      {askBaza && !readOnly ? (
        <div className="mt-3">
          <BazaAsk companyId={c.id} compact onClose={() => setAskBaza(false)} />
        </div>
      ) : null}
      {mergeOpen && !readOnly ? (
        <MergeInto
          company={c}
          flash={flash}
          onDone={() => {
            setMergeOpen(false);
            void load();
          }}
          onClose={() => setMergeOpen(false)}
        />
      ) : null}

      {/* плитки */}
      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
        {tiles.map((k) => (
          <div key={k.label} className={cn("rounded-btn border border-border bg-background/60 px-3 py-2.5", k.label === t("Дебиторка") && s && s.overdue > 0.5 && "border-amber-400/60 bg-amber-500/5")}>
            <p className="text-[11px] font-semibold text-muted-foreground">{k.label}</p>
            <p className={cn("mt-1 text-lg font-semibold tabular-nums leading-tight", k.tone)}>{k.value}</p>
            {k.sub ? <p className={cn("mt-0.5 text-[11px] text-muted-foreground", k.tone && k.label === t("Последние 12 месяцев") && k.tone)}>{k.sub}</p> : null}
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <section aria-label={t("Что происходит с клиентом")} className="min-w-0">
          <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
            {tabs.map((x) => (
              <button
                key={x.id}
                type="button"
                role="tab"
                aria-selected={tab === x.id}
                onClick={() => setTab(x.id)}
                className={cn(
                  "-mb-px cursor-pointer whitespace-nowrap rounded-t-btn border-b-2 px-3 py-2 text-[12.5px] font-semibold transition",
                  tab === x.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {x.label}
              </button>
            ))}
          </div>
          <div className="mt-3">
            {tab === "feed" ? <Feed id={c.id} /> : null}
            {tab === "dialogs" ? <ContactDialogs companyId={c.id} compact /> : null}
            {tab === "deals" ? <DealsTab companyId={c.id} contactId={primary?.id} /> : null}
            {tab === "docs" ? (
              <>
                {(c.zoho ?? []).length ? <FinanceBlock parent={{ companyId: c.id }} /> : <Empty>{t("Не связана с Books. Связать можно в разделе «Zoho» → сопоставление.")}</Empty>}
                <BazaDocs id={c.id} />
              </>
            ) : null}
            {tab === "activity" ? (
              <div className="flex flex-col gap-3">
                <TasksBlock parent={{ companyId: c.id }} onFlash={flash} />
                <NotesBlock parent={{ companyId: c.id }} onFlash={flash} />
              </div>
            ) : null}
            {tab === "history" ? <History id={c.id} /> : null}
          </div>
        </section>

        <aside className="flex min-w-0 flex-col gap-4">
          <People company={c} readOnly={readOnly} reload={load} flash={flash} />
          <Fields company={c} currency={cur} />
          <section aria-label={t("Клиенты в Zoho Books")}>
            <h4 className="text-[13px] font-semibold">{t("Клиенты в Zoho Books ({n})", { n: c.zoho?.length ?? 0 })}</h4>
            <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
              {(c.zoho ?? []).map((z) => (
                <li key={z.booksId} className="px-3 py-2">
                  <a href={z.booksUrl} target="_blank" rel="noreferrer" className="block truncate text-[12.5px] font-semibold text-primary hover:underline">
                    {z.name}
                  </a>
                  <span className="block text-[11.5px] text-muted-foreground">
                    {t("{n} счетов", { n: z.invoices })} · {aed(z.revenue)}
                    {z.receivable >= 1 ? ` · ${t("долг {sum}", { sum: aed(z.receivable) })}` : ""} · {t("связь: {why}", { why: z.linkWhy || "—" })}
                  </span>
                </li>
              ))}
              {(c.zoho ?? []).length === 0 ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("Не связана с Books. Связать можно в разделе «Zoho» → сопоставление.")}</li> : null}
            </ul>
          </section>
          <section aria-label={t("Реквизиты")}>
            <button type="button" onClick={() => setShowForm((v) => !v)} className="flex w-full cursor-pointer items-center justify-between text-[13px] font-semibold">
              {t("Реквизиты")}
              <i className={cn("ri-arrow-down-s-line text-muted-foreground transition", showForm && "rotate-180")} aria-hidden />
            </button>
            {showForm ? <Requisites id={c.id} company={c} readOnly={readOnly} reload={load} flash={flash} /> : null}
          </section>
        </aside>
      </div>
    </div>
  );
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "•";

function Chip({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-medium", ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700" : "border-border bg-background text-muted-foreground")}>{children}</span>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="rounded-btn border border-border bg-background/60 px-3 py-3 text-[12px] text-muted-foreground">{children}</p>;
}

/* ---------------------------------------------------------------- лента */

function Feed({ id }: { id: string }) {
  const [on, setOn] = useState<Set<string>>(() => new Set(FEED_FILTERS.map((f) => f.id)));
  const [items, setItems] = useState<TimelineItem[] | null>(null);
  const [error, setError] = useState("");
  const kinds = useMemo(() => FEED_FILTERS.filter((f) => on.has(f.id)).flatMap((f) => f.kinds), [on]);

  useEffect(() => {
    setError("");
    if (kinds.length === 0) {
      setItems([]);
      return;
    }
    companiesApi.timeline(id, kinds).then((r) => setItems(r.items), (e) => setError(apiErrorText(e)));
  }, [id, kinds]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
        <span className="text-muted-foreground">{t("Показать:")}</span>
        {FEED_FILTERS.map((f) => (
          <label key={f.id} className="inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={on.has(f.id)}
              onChange={(e) => {
                const next = new Set(on);
                if (e.target.checked) next.add(f.id);
                else next.delete(f.id);
                setOn(next);
              }}
              className="accent-primary"
            />
            {f.label}
          </label>
        ))}
      </div>
      {error ? <p className="mt-2 text-[12px] text-red-500">{error}</p> : null}
      {items === null ? <p className="mt-3 text-[12px] text-muted-foreground">{t("Загрузка…")}</p> : null}
      {items && items.length === 0 ? <Empty>{t("Событий пока нет: ни сообщений, ни документов, ни сделок по этому клиенту.")}</Empty> : null}
      {items && items.length > 0 ? (
        <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border" data-testid="company-feed">
          {items.map((it, i) => (
            <FeedRow key={`${it.kind}-${i}`} it={it} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const TAG: Record<TimelineItem["kind"], { label: string; cls: string }> = {
  message: { label: "", cls: "bg-emerald-500/10 text-emerald-700" },
  invoice: { label: t("Счёт"), cls: "bg-sky-500/10 text-sky-700" },
  order: { label: t("Заказ"), cls: "bg-indigo-500/10 text-indigo-700" },
  payment: { label: t("Оплата"), cls: "bg-emerald-500/10 text-emerald-700" },
  creditnote: { label: t("Кредит-нота"), cls: "bg-slate-500/10 text-slate-700" },
  deal: { label: t("Сделка"), cls: "bg-purple-500/10 text-purple-700" },
  note: { label: t("Заметка"), cls: "bg-amber-500/10 text-amber-700" },
  task: { label: t("Задача"), cls: "bg-orange-500/10 text-orange-700" },
};

const docStatus = (s: string) =>
  ({ paid: t("оплачен"), overdue: t("просрочен"), sent: t("отправлен"), draft: t("черновик"), void: t("отменён"), partially_paid: t("оплачен частично"), unpaid: t("не оплачен"), open: t("открыт"), invoiced: t("выставлен счёт"), confirmed: t("подтверждён"), closed: t("закрыт") })[s] ?? s;

function FeedRow({ it }: { it: TimelineItem }) {
  const tag = TAG[it.kind];
  let body: React.ReactNode;
  let onClick: (() => void) | undefined;
  if (it.kind === "message") {
    onClick = () => {
      showConversation(it.conversationId);
      goAnchor("#inbox-app");
    };
    body = (
      <>
        <span className="font-semibold">{it.fromMe ? t("мы") : it.who || t("клиент")}:</span> {it.text || (it.media ? t("[файл]") : "")}
      </>
    );
  } else if (it.kind === "invoice" || it.kind === "order" || it.kind === "creditnote") {
    body = (
      <>
        <a href={it.booksUrl} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline">
          {it.number}
        </a>{" "}
        · {money(it.total, it.currency)} · {docStatus(it.status)}
        {it.kind === "invoice" && it.dueDate ? ` · ${t("срок {date}", { date: day(it.dueDate) })}` : ""}
        {it.kind === "invoice" && it.balance > 0.5 ? <span className="font-semibold text-amber-700"> · {t("остаток {sum}", { sum: money(it.balance, it.currency) })}</span> : null}
      </>
    );
  } else if (it.kind === "payment") {
    body = (
      <>
        <a href={it.booksUrl} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline">
          {it.number}
        </a>{" "}
        · {money(it.total, it.currency)}
        {it.mode ? ` · ${it.mode}` : ""}
        {it.invoiceNumbers?.length ? ` → ${it.invoiceNumbers.join(", ")}` : ""}
      </>
    );
  } else if (it.kind === "deal") {
    onClick = () => {
      showDeal(it.dealId);
      goAnchor("#deals");
    };
    body = (
      <>
        <span className="font-semibold">«{it.title}»</span>
        {it.event === "stage" ? `: ${it.from || "—"} → ${it.to}` : `: ${t("создана")}${it.stage ? ` · ${it.stage}` : ""}`}
        {it.amount !== null && it.amount !== undefined ? ` · ${money(it.amount, it.currency)}` : ""}
        {it.by ? ` · ${it.by}` : ""}
      </>
    );
  } else if (it.kind === "note") {
    body = (
      <>
        {it.crm ? <span className="text-muted-foreground">{t("из CRM")} · </span> : null}
        {it.title ? <span className="font-semibold">{it.title}: </span> : null}«{it.text}»{it.by ? <span className="text-muted-foreground"> · {it.by}</span> : null}
      </>
    );
  } else {
    body = (
      <>
        <span className="font-semibold">{it.title}</span>
        {it.status === "done" ? ` · ${t("выполнена")}` : it.dueDate ? ` · ${t("срок {date}", { date: day(it.dueDate) })}` : ""}
        {it.assignee ? ` · ${it.assignee}` : ""}
      </>
    );
  }
  const Row = onClick ? "button" : "div";
  return (
    <li>
      <Row type={onClick ? "button" : undefined} onClick={onClick} className={cn("flex w-full items-start gap-3 px-3 py-2 text-start text-[12.5px]", onClick && "cursor-pointer transition hover:bg-muted/60")}>
        <span className="w-[6.5rem] flex-none pt-0.5 text-[11px] tabular-nums text-muted-foreground">{when(it.at)}</span>
        <span className={cn("flex-none rounded px-1.5 py-0.5 text-[10.5px] font-semibold", tag.cls)}>{it.kind === "message" ? channelLabel(it.channel) : tag.label}</span>
        <span className="min-w-0 flex-1 leading-relaxed">{body}</span>
      </Row>
    </li>
  );
}

/* ---------------------------------------------------------------- сделки */

function DealsTab({ companyId, contactId }: { companyId: string; contactId?: string }) {
  const [rows, setRows] = useState<Deal[] | null>(null);
  useEffect(() => {
    dealsApi.list({ company: companyId, size: 100 }).then((r) => setRows(r.items), () => setRows([]));
  }, [companyId]);
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-[12px] text-muted-foreground">{rows ? t("{n} сделок компании и её людей", { n: rows.length }) : t("Загрузка…")}</span>
        <button
          type="button"
          onClick={() => {
            showNewDeal({ companyId, contactId: contactId ?? null });
            goAnchor("#deals");
          }}
          className="cursor-pointer text-[12px] font-semibold text-primary hover:underline"
        >
          + {t("Сделка")}
        </button>
      </div>
      <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
        {(rows ?? []).map((d) => (
          <li key={d.id}>
            <button
              type="button"
              onClick={() => {
                showDeal(d.id);
                goAnchor("#deals");
              }}
              className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start text-[12.5px] transition hover:bg-muted/60"
            >
              <span className="size-2 flex-none rounded-full" style={{ background: tagColors.find((x) => x.id === d.stage?.color)?.hex ?? tagColors[0].hex }} aria-hidden />
              <span className="min-w-0 flex-1 truncate font-semibold">{d.title}</span>
              <span className="text-[11px] text-muted-foreground">{d.stage?.name}</span>
              {d.amount !== null ? <span className="text-[11.5px] tabular-nums text-muted-foreground">{money(d.amount, d.currency)}</span> : null}
              <span className="w-20 text-end text-[11px] tabular-nums text-muted-foreground">{day(d.closingDate ?? d.createdAt)}</span>
            </button>
          </li>
        ))}
        {rows && rows.length === 0 ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("Сделок пока нет.")}</li> : null}
      </ul>
    </div>
  );
}

/* ---------------------------------------------------------------- история */

const ACTION: Record<string, string> = {
  "company.create": t("компания создана"),
  "company.update": t("реквизиты изменены"),
  "company.owner": t("ответственный изменён"),
  "company.person": t("человек добавлен или роль изменена"),
  "company.person_remove": t("человек убран из компании"),
  "deal.create": t("сделка создана"),
  "deal.update": t("сделка изменена"),
  "deal.stage": t("этап сделки изменён"),
  "deal.delete": t("сделка удалена"),
};

/* Документы из базы знаний 3Jar (baza.starec.ai) по названию компании: инвойсы, проформы, договоры, прайсы.
   Ссылки ведут в базу — нужен вход туда. Пока база не подключена на сервере, блок не показывается. */
function BazaDocs({ id }: { id: string }) {
  const [data, setData] = useState<BazaDocuments | null>(null);
  useEffect(() => {
    setData(null);
    companiesApi.bazaDocuments(id).then(setData).catch(() => setData({ enabled: false, items: [], total: 0, searchUrl: "" }));
  }, [id]);
  if (!data || !data.enabled) return null;
  return (
    <section className="mt-4" aria-label={t("Документы в базе знаний")} data-testid="company-baza-docs">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-[13px] font-semibold">{t("В базе знаний ({n})", { n: data.total })}</h4>
        <a href={data.searchUrl} target="_blank" rel="noreferrer" className="text-[12px] text-primary hover:underline">
          {t("Открыть базу знаний")}
        </a>
      </div>
      {data.error ? <p className="mt-1 text-[12px] text-muted-foreground">{t("База знаний недоступна, попробуйте позже")}</p> : null}
      {data.items.length === 0 && !data.error ? <p className="mt-1 text-[12px] text-muted-foreground">{t("Документов по этой компании в базе знаний нет")}</p> : null}
      {data.items.length ? (
        <ul className="mt-1.5 divide-y divide-border rounded-md border border-border">
          {data.items.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1.5 text-[12px]">
              <span className="w-20 shrink-0 text-muted-foreground">{day(d.date)}</span>
              <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{d.kind}</span>
              <a href={d.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-foreground hover:underline" title={d.title}>
                {d.title}
              </a>
              {d.number ? <span className="shrink-0 text-muted-foreground">{d.number}</span> : null}
              {d.copies > 1 ? <span className="shrink-0 text-muted-foreground">{t("копий: {n}", { n: d.copies })}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function History({ id }: { id: string }) {
  const [rows, setRows] = useState<HistoryItem[] | null>(null);
  useEffect(() => {
    companiesApi.history(id).then((r) => setRows(r.items), () => setRows([]));
  }, [id]);
  const describe = (h: HistoryItem) => {
    if (h.action === "company.owner") return `${(h.details.fromName as string) || t("никто")} → ${(h.details.toName as string) || t("никто")}`;
    const changes = Object.entries(h.details)
      .filter(([k, v]) => k !== "name" && v && typeof v === "object" && "to" in (v as object))
      .map(([k, v]) => `${k}: ${(v as { from?: string }).from || "—"} → ${(v as { to?: string }).to || "—"}`);
    return changes.length ? changes.join("; ") : (h.details.name as string) || "";
  };
  return (
    <ul className="flex flex-col divide-y divide-border/60 rounded-btn border border-border">
      {(rows ?? []).map((h, i) => (
        <li key={i} className="flex items-start gap-3 px-3 py-2 text-[12.5px]">
          <span className="w-[6.5rem] flex-none pt-0.5 text-[11px] tabular-nums text-muted-foreground">{when(h.at)}</span>
          <span className="min-w-0 flex-1">
            <span className="font-semibold">{ACTION[h.action] ?? h.action}</span>
            {h.entity === "deal" ? <span className="text-muted-foreground"> · {t("сделка")}</span> : null}
            {describe(h) ? <span className="block truncate text-[11.5px] text-muted-foreground">{describe(h)}</span> : null}
          </span>
          <span className="text-[11px] text-muted-foreground">{h.actor ?? t("система")}</span>
        </li>
      ))}
      {rows && rows.length === 0 ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("Изменений в Hub пока не было.")}</li> : null}
      {rows === null ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("Загрузка…")}</li> : null}
    </ul>
  );
}

/* ---------------------------------------------------------------- правая колонка */

/* «Слить с…»: найти компанию-дубль по названию и влить её в эту карточку — её люди, сделки, заметки, задачи и клиенты Books
   переходят сюда, карточка дубля удаляется (аккаунт Zoho CRM дубля запоминается, синхронизация не создаст его заново). */
function MergeInto({ company: c, flash, onDone, onClose }: { company: Company; flash: (t: string) => void; onDone: () => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Company[]>([]);
  const [pick, setPick] = useState<Company | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!q.trim()) {
      setFound([]);
      return;
    }
    const tm = setTimeout(() => {
      companiesApi.list({ q: q.trim(), size: 8 }).then((r) => setFound(r.items.filter((x) => x.id !== c.id)), () => setFound([]));
    }, 250);
    return () => clearTimeout(tm);
  }, [q, c.id]);

  const merge = async () => {
    if (!pick || busy) return;
    setBusy(true);
    try {
      const r = await companiesApi.merge(c.id, [pick.id]);
      flash(t("Слито: «{name}» → «{keep}»", { name: pick.name, keep: r.name }));
      onDone();
    } catch (e) {
      flash(apiErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-btn border border-border bg-background/60 p-3" data-testid="company-merge-panel">
      <p className="text-[12.5px] font-semibold">{t("Слить другую компанию в эту карточку")}</p>
      <p className="mt-0.5 text-[12px] text-muted-foreground">{t("Найдите компанию-дубль: её люди, сделки, заметки, задачи и клиенты Books перейдут сюда, а её карточка будет удалена. Пустые реквизиты этой карточки заполнятся из дубля.")}</p>
      {!pick ? (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Название компании-дубля")} aria-label={t("Название компании-дубля")} autoFocus className={cn(input, "mt-2 max-w-md")} data-testid="company-merge-search" />
          {found.length ? (
            <ul className="mt-2 flex max-w-md flex-col divide-y divide-border rounded-btn border border-border">
              {found.map((x) => (
                <li key={x.id}>
                  <button type="button" onClick={() => setPick(x)} className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-start text-[12.5px] hover:bg-muted/60" data-testid="company-merge-option">
                    <span className="min-w-0 truncate font-semibold">{x.name}</span>
                    <span className="flex-none text-[11px] text-muted-foreground">
                      {[x.country, tn(x.people || 0, "человек", "человека", "человек"), x.revenue ? aed(x.revenue) : ""].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : q.trim() ? (
            <p className="mt-2 text-[12px] text-muted-foreground">{t("Ничего не найдено")}</p>
          ) : null}
        </>
      ) : (
        <div className="mt-2 rounded-btn border border-amber-400/60 bg-amber-500/5 p-3 text-[12.5px]">
          {t("Компания «{name}» перейдёт в карточку «{keep}»: её люди, сделки, заметки, задачи и клиенты Books. Карточка «{name}» будет удалена.", { name: pick.name, keep: c.name })}
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => void merge()} disabled={busy} className={btnPrimary} data-testid="company-merge-confirm">
              <i className="ri-git-merge-line" aria-hidden />
              {t("Слить компании")}
            </button>
            <button type="button" onClick={() => setPick(null)} className={btnGhost}>
              {t("Выбрать другую")}
            </button>
          </div>
        </div>
      )}
      <button type="button" onClick={onClose} className="mt-2 cursor-pointer text-[12px] font-semibold text-muted-foreground hover:text-foreground">
        {t("Закрыть")}
      </button>
    </div>
  );
}

function People({ company: c, readOnly, reload, flash }: { company: Company; readOnly: boolean; reload: () => Promise<void>; flash: (t: string) => void }) {
  const [find, setFind] = useState("");
  const [found, setFound] = useState<Contact[]>([]);
  useEffect(() => {
    if (find.trim().length < 2) {
      setFound([]);
      return;
    }
    const tm = setTimeout(() => contactsApi.list({ q: find, limit: 8 }).then((r) => setFound(r.items), () => undefined), 250);
    return () => clearTimeout(tm);
  }, [find]);
  const act = async (fn: () => Promise<unknown>, text: string) => {
    try {
      await fn();
      await reload();
      flash(text);
    } catch (e) {
      flash(apiErrorText(e));
    }
  };
  const people = c.contacts ?? [];
  return (
    <section aria-label={t("Контактные лица")}>
      <h4 className="text-[13px] font-semibold">{t("Контактные лица")}{people.length ? ` · ${people.length}` : ""}</h4>
      <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
        {people.map((p) => (
          <li key={p.id} className="px-3 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  showContact(p.id);
                  goAnchor("#contacts-app");
                }}
                className="min-w-0 flex-1 cursor-pointer text-start text-[12.5px] font-semibold hover:underline"
              >
                {p.name}
              </button>
              <select
                value={p.companyRole}
                disabled={readOnly}
                onChange={(e) => void act(() => companiesApi.attach(c.id, p.id, e.target.value), t("Роль сохранена"))}
                aria-label={t("Роль: {name}", { name: p.name })}
                className="h-7 rounded-btn border border-border bg-background px-1.5 text-[11.5px]"
              >
                <option value="">{t("Роль не указана")}</option>
                {Object.entries(PERSON_ROLES).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => void act(() => companiesApi.detach(c.id, p.id), t("{name} больше не в компании", { name: p.name }))}
                  aria-label={t("Убрать {name} из компании", { name: p.name })}
                  className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  <i className="ri-close-line" aria-hidden />
                </button>
              ) : null}
            </div>
            <p className="mt-0.5 text-[11.5px] text-muted-foreground">{[p.phone, p.email].filter(Boolean).join(" · ") || t("без контактов")}</p>
          </li>
        ))}
        {people.length === 0 ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("Пока никого — добавьте человека из контактов.")}</li> : null}
      </ul>
      {c.hiddenContacts ? <p className="mt-1 text-[11px] text-muted-foreground">{t("Ещё {n} — у других продавцов.", { n: c.hiddenContacts })}</p> : null}
      {!readOnly ? (
        <div className="relative mt-2">
          <input value={find} onChange={(e) => setFind(e.target.value)} placeholder={t("+ Контакт: имя, телефон или почта")} aria-label={t("Найти контакт")} className={cn(input, "h-8 text-[12px]")} />
          {found.length > 0 ? (
            <ul className="absolute inset-x-0 top-full z-10 mt-1 max-h-64 overflow-auto rounded-btn border border-border bg-card shadow-lg">
              {found.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setFind("");
                      void act(() => companiesApi.attach(c.id, p.id, "buyer"), t("{name} добавлен в компанию", { name: p.name }));
                    }}
                    className="flex w-full cursor-pointer flex-col px-3 py-2 text-start transition hover:bg-muted"
                  >
                    <span className="text-[12.5px] font-semibold">{p.name}</span>
                    <span className="text-[11.5px] text-muted-foreground">{[p.phone, p.company].filter(Boolean).join(" · ")}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function Fields({ company: c, currency }: { company: Company; currency: string }) {
  const rows: [string, string][] = [
    [t("Сегмент"), c.segment],
    [t("Направление"), c.department],
    [t("Источник"), c.leadSource],
    [t("Продавец Zoho"), c.salesperson ?? ""],
    [t("Страна"), c.country],
    ["TRN", c.trn],
    [t("Валюта"), currency],
  ];
  return (
    <section aria-label={t("Поля")}>
      <h4 className="text-[13px] font-semibold">{t("Поля")}</h4>
      <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-btn border border-border px-3 py-2 text-[12px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="truncate text-end font-medium">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Requisites({ id, company: c, readOnly, reload, flash }: { id: string; company: Company; readOnly: boolean; reload: () => Promise<void>; flash: (t: string) => void }) {
  const [form, setForm] = useState<CompanyInput>(() => Object.fromEntries(FIELDS.map((f) => [f.key, (c[f.key as keyof Company] as string | null) ?? ""])));
  const [saving, setSaving] = useState(false);
  const dirty = FIELDS.some((f) => (form[f.key] ?? "") !== ((c[f.key as keyof Company] as string | null) ?? ""));
  const save = async () => {
    setSaving(true);
    try {
      const changed = Object.fromEntries(FIELDS.filter((f) => (form[f.key] ?? "") !== ((c[f.key as keyof Company] as string | null) ?? "")).map((f) => [f.key, form[f.key]]));
      await companiesApi.update(id, changed);
      await reload();
      flash(t("Карточка компании сохранена"));
    } catch (e) {
      flash(apiErrorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-2 grid grid-cols-1 gap-2">
      {FIELDS.map((f) => (
        <label key={f.key} className="block">
          <span className="text-[11.5px] font-semibold text-muted-foreground">{f.label}</span>
          {f.key === "notes" ? (
            <textarea value={form[f.key] ?? ""} disabled={readOnly} onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))} rows={3} className={cn(input, "mt-1 h-auto py-2")} />
          ) : (
            <input value={form[f.key] ?? ""} disabled={readOnly} onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))} className={cn(input, "mt-1")} />
          )}
        </label>
      ))}
      {!readOnly ? (
        <button type="button" disabled={!dirty || saving} onClick={() => void save()} className={cn(btnPrimary, "self-start")}>
          {saving ? t("Сохраняю…") : t("Сохранить")}
        </button>
      ) : null}
    </div>
  );
}
